'use client';

import { type ComponentPropsWithoutRef, type ReactNode, useState } from 'react';

import { Sentry } from '@lib/sentry';
import { toProxyUrl } from '@lib/util/image-proxy';

interface ProxiedImageProps extends Omit<ComponentPropsWithoutRef<'img'>, 'src' | 'onError'> {
  /** Original image URL (proxied via toProxyUrl; falls back to direct URL if proxy URL generation fails) */
  url: string;
  /** Label for error logging (e.g. "Hero", "RichContent") */
  label?: string;
  /** Custom fallback UI when image fails to load */
  fallback?: ReactNode;
}

const DEFAULT_FALLBACK = (
  <div className="flex h-full w-full items-center justify-center rounded-md bg-gray-100 text-sm text-gray-400">
    圖片載入失敗
  </div>
);

const ProxiedImage = ({ url, label = 'ProxiedImage', fallback = DEFAULT_FALLBACK, alt, ...imgProps }: ProxiedImageProps) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={toProxyUrl(url) ?? url}
      alt={alt ?? ''}
      onError={() => {
        console.warn(`[${label}] Failed to load image:`, url);
        Sentry.captureMessage(`Image failed to load: ${url}`, {
          level: 'warning',
          tags: { component: label },
        });
        setHasError(true);
      }}
      {...imgProps}
    />
  );
};

export default ProxiedImage;
